import camelcaseKeys from 'camelcase-keys';
import { createStore } from 'dashboard/store/storeFactory';
import { throwErrorMessage } from 'dashboard/store/utils/api';
import DealsAPI from 'dashboard/api/dealsApi';

const ALL_PAGE_SIZE = 100;

const normalizeMeta = meta => ({
  ...camelcaseKeys(meta || {}),
  totalCount: Number(meta?.total_count || meta?.totalCount || meta?.count || 0),
  page: Number(meta?.page || meta?.current_page || 1),
});

const camelizeDeal = data =>
  camelcaseKeys(data || {}, { deep: true, stopPaths: ['custom_attributes'] });

export const useDealsStore = createStore({
  name: 'deals',
  type: 'pinia',
  API: DealsAPI,
  state: () => ({}),

  getters: {
    getDealsList: state => state.records,
    getDealsByStage: state => stageId =>
      state.records.filter(d => d.pipelineStageId === stageId),
  },

  actions: () => ({
    setMeta(meta) {
      this.meta = normalizeMeta(meta);
    },

    upsertDeal(record) {
      const index = this.records.findIndex(r => r.id === record.id);
      if (index === -1) this.records.push(record);
      else this.records[index] = record;
    },

    async get({ page = 1, sort = '-created_at', search, pipeline_id } = {}) {
      this.setUIFlag({ fetchingList: true });
      try {
        const {
          data: { payload, meta },
        } = await DealsAPI.get({ page, sort, search, pipeline_id });
        this.records = camelizeDeal(payload);
        this.setMeta(meta);
        return this.records;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async getAll({ search, pipeline_id }) {
      this.setUIFlag({ fetchingList: true });
      try {
        const params = { search, pipeline_id, per_page: ALL_PAGE_SIZE };
        const first = await DealsAPI.get({ ...params, page: 1 });
        const totalCount = Number(first.data.meta.total_count);
        const lastPage = Math.ceil(totalCount / ALL_PAGE_SIZE);
        const rest = await Promise.all(
          Array.from({ length: Math.max(lastPage - 1, 0) }, (_, index) =>
            DealsAPI.get({ ...params, page: index + 2 })
          )
        );
        this.records = [first, ...rest].flatMap(({ data }) =>
          camelizeDeal(data.payload)
        );
        this.setMeta(first.data.meta);
        return this.records;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async getByContact(contactId) {
      const {
        data: { payload },
      } = await DealsAPI.get({ contact_id: contactId, sort: '-created_at' });
      return camelizeDeal(payload);
    },

    async getByCompany(companyId) {
      const {
        data: { payload },
      } = await DealsAPI.get({ company_id: companyId, sort: '-created_at' });
      return camelizeDeal(payload);
    },

    async show(id) {
      const {
        data: { payload },
      } = await DealsAPI.show(id);
      return camelizeDeal(payload);
    },

    async create(dealAttrs) {
      this.setUIFlag({ creatingItem: true });
      try {
        const {
          data: { payload },
        } = await DealsAPI.create(dealAttrs);
        const deal = camelizeDeal(payload);
        this.upsertDeal(deal);
        return deal;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ creatingItem: false });
      }
    },

    async update({ id, ...dealAttrs }) {
      this.setUIFlag({ updatingItem: true });
      try {
        const {
          data: { payload },
        } = await DealsAPI.update(id, dealAttrs);
        const deal = camelizeDeal(payload);
        this.upsertDeal(deal);
        return deal;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ updatingItem: false });
      }
    },

    async delete(id) {
      this.setUIFlag({ deletingItem: true });
      try {
        await DealsAPI.destroy(id);
        this.records = this.records.filter(r => r.id !== Number(id));
        return Number(id);
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ deletingItem: false });
      }
    },

    async import({ file, fieldMapping }) {
      this.setUIFlag({ isImporting: true });
      try {
        await DealsAPI.importDeals({ file, fieldMapping });
        return true;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ isImporting: false });
      }
    },

    async export(ids) {
      this.setUIFlag({ isExporting: true });
      try {
        const response = await DealsAPI.exportDeals(ids);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const today = new Date().toISOString().slice(0, 10);
        link.setAttribute('download', `deals_${today}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        const text = await error.response?.data?.text?.();
        const message = text ? JSON.parse(text)?.message : null;
        throw new Error(message || error.message);
      } finally {
        this.setUIFlag({ isExporting: false });
      }
    },
  }),
});
